import cx from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import type { SimpleInterpolation } from 'styled-components';
import styled, { css } from 'styled-components';
import { Device } from '../../device';
import { Theme } from '../../theme';
import { Tooltip } from '../tooltip';
import { Descriptor } from './descriptor';

const StyledWrapper = styled.div<{ $isMobile: boolean }>`
  ${({ $isMobile }): SimpleInterpolation =>
    $isMobile
      ? css`
          display: flex;
          flex-direction: column;
          align-items: flex-end;

          .customization-switch {
            padding-left: 3rem;
            margin-bottom: 4px;
          }
        `
      : css`
          display: flex;
          align-items: center;
        `}

  /* Set switch cursor style to pointer. */
  .custom-switch .custom-control-input {
    z-index: 1;
    width: 100%;
    height: 100%;

    &,
    &:checked {
      & ~ .custom-control-label::before {
        cursor: pointer;
      }
    }
  }

  .save-indicator {
    transition: 0.2s opacity;
    opacity: 0;

    &.saved {
      opacity: 1;
    }
  }
`;

const LockIcon = ({ className }: { className?: string }): React.ReactElement => (<svg className={`ml-16px mr-md-8px ${className ?? ''}`} xmlns="http://www.w3.org/2000/svg" width="22" height="28" viewBox="0 0 22 28" fill="none">
    <path d="M6.22732 11.3784H3.50005C2.80971 11.3784 2.25007 11.9356 2.25006 12.626C2.25001 14.6854 2.24995 18.8465 2.25008 21.6486C2.25023 24.7896 7.10767 25.5 10.9999 25.5C14.8923 25.5 19.7499 24.7896 19.7499 21.6486C19.7499 18.8466 19.7499 14.6854 19.7499 12.626C19.7499 11.9356 19.1903 11.3784 18.4999 11.3784H15.7727M6.22732 11.3784C6.22732 11.3784 6.22736 8.16893 6.22732 6.24324C6.22727 3.93214 8.13603 1.75 10.9999 1.75C13.8639 1.75 15.7727 3.93214 15.7727 6.24324C15.7727 8.16893 15.7727 11.3784 15.7727 11.3784M6.22732 11.3784H15.7727" stroke="black" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    <path fillRule="evenodd" clipRule="evenodd" d="M12.25 18.7726V20.5C12.25 21.1904 11.6904 21.75 11 21.75C10.3096 21.75 9.75 21.1904 9.75 20.5V18.7726C9.36637 18.4292 9.125 17.9302 9.125 17.375C9.125 16.3395 9.9645 15.5 11 15.5C12.0355 15.5 12.875 16.3395 12.875 17.375C12.875 17.9302 12.6336 18.4292 12.25 18.7726Z" fill="black"/>
</svg>);

const TooltipWrapper = ({ tooltip, show, children }: { tooltip?: string; name?:string,show?: boolean, children: React.ReactElement<unknown, string> }): React.ReactElement => {
  if (tooltip && show) {
    return (
      <Tooltip tip={tooltip}>
      {children}
    </Tooltip>
    );
  }

  return (<>{React.Children.toArray(children)}</>);
};

export const SwitchField = ({
  title,
  titleClassName,
  description,
  descriptionClassName,
  disabled,
  checked,
  onChange,
  loading,
  className,
  noSaveIndicator = false,
  name,
  abledDescription = true,
  spaceInBetween = true,
  locked = false,
  tooltip
}: SwitchField.SwitchFieldProps): React.ReactElement => {
  const viewport = Theme.useViewport();
  const isMobile = Device.isMobile(viewport);

  const [saved, setSaved] = useState(false);
  const [isModal, setIsModal] = useState(false);

  const handleChange = useCallback(
    (value: boolean) => (): void => {
      const result = onChange(value);

      if (result && !disabled) {
        result
          .then((modal) => {
            if (modal) {
              setIsModal(modal);
            } else {
              setSaved(true);
            }
          })
          .catch(() => {
            // Ignore.
          });
      }
    },
    [disabled, onChange]
  );

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;

    if (checked && isModal) {
      setSaved(true);
      setIsModal(false);
    }

    if (saved) {
      timeout = setTimeout(() => {
        setSaved(false);
      }, 3000);
    }

    return (): void => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [saved, checked, isModal]);

  return abledDescription ? (
    <button
      type='button'
      className='x-pseudo text-left'
      disabled={disabled}
      style={{ width: '100%' }}
      onClick={handleChange(!checked)}
    >
      <Descriptor
        title={title}
        titleClassName={titleClassName}
        description={description}
        descriptionClassName={cx(descriptionClassName, 'ml-10px mr-20px')}
        className={cx('d-flex align-items-start', 'cursor-pointer', className)}
      >
        {spaceInBetween ? <div className='flex-grow-1' /> : null}
        <StyledWrapper $isMobile={isMobile}>
          <TooltipWrapper tooltip={tooltip} name={name} show={disabled}>
            {!locked ?
              <Form.Switch
                className='customization-switch'
                checked={checked}
                disabled={disabled}
                onChange={handleChange(!checked)}
                style={{
                  opacity: loading || disabled ? 0.5 : 1,
                  pointerEvents: 'none'
                }}
                name={name}
              />
              : <span>
                <LockIcon/>
              </span>
            }
          </TooltipWrapper>
          <span
            className={cx(
              'text-success font-weight-bold text-center font-size-12px save-indicator',
              'ml-md-8px',
              {
                saved,
                'd-none': noSaveIndicator
              }
            )}
            style={{
              whiteSpace: 'nowrap'
            }}
          >
            <i className='mr-8px fa fa-check' />
            Saved
          </span>
        </StyledWrapper>
      </Descriptor>
    </button>
  ) : (
    <Descriptor
      title={title}
      titleClassName={titleClassName}
      description={description}
      descriptionClassName={descriptionClassName}
      className={cx('d-flex align-items-start', className)}
    >
      {spaceInBetween ? <div className='flex-grow-1' /> : null}
      <button
        type='button'
        className='x-pseudo text-left'
        disabled={disabled}
        onClick={handleChange(!checked)}
      >
        <StyledWrapper $isMobile={isMobile}>
          <TooltipWrapper tooltip={tooltip} show={disabled} name={name}>
            {!locked ? 
              <Form.Switch
                className='customization-switch'
                checked={checked}
                disabled={disabled}
                onChange={handleChange(!checked)}
                style={{
                  opacity: loading || disabled ? 0.5 : 1,
                  pointerEvents: tooltip ? 'auto' : 'none'
                }}
                name={name}
              /> 
            : <span>
                <LockIcon/>
              </span>
            }
          </TooltipWrapper>
          <span
            className={cx(
              'text-success font-weight-bold text-center font-size-12px save-indicator',
              'ml-md-8px',
              {
                saved,
                'd-none': noSaveIndicator
              }
            )}
            style={{
              whiteSpace: 'nowrap'
            }}
          >
            <i className='mr-8px fa fa-check' />
            Saved
          </span>
        </StyledWrapper>
      </button>
    </Descriptor>
  );
};

// eslint-disable-next-line @typescript-eslint/no-redeclare
export namespace SwitchField {
  export interface SwitchFieldProps {
    abledDescription?: boolean;
    title: React.ReactNode;
    titleClassName?: string;
    description?: React.ReactNode;
    descriptionClassName?: string;
    checked: boolean;
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    onChange: (value: boolean) => Promise<boolean | void> | void;
    loading?: boolean;
    disabled?: boolean;
    className?: string;
    noSaveIndicator?: boolean;
    name?: string;
    spaceInBetween?: boolean;
    locked?: boolean
    tooltip?: string;
  }
}
